import React, { useState, useEffect, useMemo, useRef } from 'react';
import type { ProductItem, ColorVariant } from './types';
import { sanitizeUrl, getFallbackImageUrl, getProxiedImageUrl } from './utils';
import { useImagePreloader } from '../../hooks/useImagePreloader';

interface ProductCardDetailedProps {
  product: ProductItem;
}

const ProductCardDetailedComponent: React.FC<ProductCardDetailedProps> = ({ product }) => {
  // Derive a preferred variant index using requested color context or backend-provided default
  const computePreferredIndex = (): number => {
    const variants = product.color_variants || [];
    if (!variants.length) return 0;

    // Backend hint (e.g., matched partialSku)
    const backendDefault = (product as any).default_variant_index;
    if (typeof backendDefault === 'number' && backendDefault >= 0 && backendDefault < variants.length) {
      // Even with backend default, prefer one with an image if the default lacks one
      const defaultVariant = variants[backendDefault];
      if (defaultVariant.image_url) {
        return backendDefault;
      }
      // Backend default has no image - look for one that does
      const withImage = variants.findIndex(v => v.image_url);
      return withImage >= 0 ? withImage : backendDefault;
    }

    const normalize = (v?: string | null) => (v ? v.toString().toLowerCase().replace(/[^a-z0-9]/g, '') : '');
    const requestedPool: string[] = [];

    // Possible color hints attached to the product
    const maybeColors = [
      (product as any).requested_color,
      (product as any).requested_color_code,
      (product as any).color_filter,
      product.attributes?.color_keywords,
      ...(Array.isArray((product as any).requested_colors) ? (product as any).requested_colors : []),
      ...(Array.isArray((product as any).query_color_codes) ? (product as any).query_color_codes : []),
      ...(Array.isArray((product as any).requested_color_names) ? (product as any).requested_color_names : []),
    ].filter(Boolean) as string[];

    maybeColors.forEach(c => {
      const norm = normalize(c);
      if (norm) requestedPool.push(norm);
    });

    // If we have color preferences, find matching variant
    if (requestedPool.length > 0) {
      // First try to find a matching variant that also has an image
      for (let i = 0; i < variants.length; i++) {
        const v = variants[i];
        const colorCandidates = [v.color_name, v.color_code, (v as any).variant_color_code];
        const variantNorms = colorCandidates.map(normalize).filter(Boolean);
        if (variantNorms.some(c => requestedPool.includes(c)) && v.image_url) {
          return i;
        }
      }
      // Fallback: matching variant without image
      for (let i = 0; i < variants.length; i++) {
        const v = variants[i];
        const colorCandidates = [v.color_name, v.color_code, (v as any).variant_color_code];
        const variantNorms = colorCandidates.map(normalize).filter(Boolean);
        if (variantNorms.some(c => requestedPool.includes(c))) {
          return i;
        }
      }
    }

    // No color preference - prefer first variant with a valid image
    const firstWithImage = variants.findIndex(v => v.image_url);
    if (firstWithImage >= 0) {
      return firstWithImage;
    }

    // Last resort: first variant
    return 0;
  };

  const defaultIndex = computePreferredIndex();
  const [selected, setSelected] = useState<ColorVariant | null>(
    product.color_variants && product.color_variants.length > 0 ? product.color_variants[defaultIndex] : null
  );
  const [isReasonsExpanded, setIsReasonsExpanded] = useState(false);

  // Initialize image preloader
  const {
    preloadImage,
    isImageLoaded,
    isImageLoading,
    getImageError,
    cacheVersion
  } = useImagePreloader({ preloadPriority: 'high' });

  const cardRef = useRef<HTMLDivElement>(null);
  const [hasEnteredView, setHasEnteredView] = useState(false);

  // Get image URL and ensure it's properly encoded
  const rawImageUrl = selected?.image_url || product.primary_image || undefined;
  
  // First sanitize the URL to ensure proper encoding of SAS tokens
  const sanitizedUrl = sanitizeUrl(rawImageUrl);
  
  // Then proxy the URL to avoid CORS issues
  const imageUrl = getProxiedImageUrl(sanitizedUrl);
  
  // Get fallback image based on product category
  const fallbackImageUrl = getFallbackImageUrl(product.attributes?.category);
  
  const sku = selected?.sku || product.partialSku_OPIM || product.base_sku;

  // Start loading images only when the card is on screen
  useEffect(() => {
    if (hasEnteredView) return;
    const node = cardRef.current;

    if (!node || typeof IntersectionObserver === 'undefined') {
      setHasEnteredView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setHasEnteredView(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '200px 0px 200px 0px', threshold: 0.1 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasEnteredView]);

  // Check image status using preloader
  // IMPORTANT: Use the proxied imageUrl (what's actually rendered), not rawImageUrl!
  // This ensures the cache key matches what the browser is actually loading.
  const imageLoaded = useMemo(
    () => imageUrl ? isImageLoaded(imageUrl) : false,
    [imageUrl, cacheVersion]
  );
  const imageIsLoading = useMemo(
    () => imageUrl ? isImageLoading(imageUrl) : false,
    [imageUrl, cacheVersion]
  );
  const imageError = useMemo(
    () => imageUrl ? getImageError(imageUrl) : false,
    [imageUrl, cacheVersion]
  );

  // Always preload the current image (handles single-color products and variant changes)
  // IMPORTANT: Preload the proxied imageUrl (what's actually rendered), not rawImageUrl!
  useEffect(() => {
    if (hasEnteredView && imageUrl) {
      preloadImage(imageUrl);
    }
  }, [hasEnteredView, imageUrl, preloadImage]);

  // Preload swatches only after the card is visible
  // IMPORTANT: Preload the proxied URLs (what's actually rendered)
  useEffect(() => {
    if (!hasEnteredView) return;
    if (product.color_variants && product.color_variants.length > 0) {
      product.color_variants.forEach((v: any) => {
        if (v?.swatch_url) {
          const proxiedSwatchUrl = getProxiedImageUrl(sanitizeUrl(v.swatch_url));
          if (proxiedSwatchUrl) preloadImage(proxiedSwatchUrl);
        }
      });
    }
  }, [hasEnteredView, product.color_variants, preloadImage]);

  // Handle color variant selection
  const handleVariantSelect = (variant: ColorVariant) => {
    setSelected(variant);
  };

  // Extract badges and reasons from product data
  const badges = product.attributes?.badges || [];
  const reasons = product.reasons || [];
  const stockItems = product.attributes?.stock_items || 0;
  const madeByUniFirst = (product.attributes as any)?.made_by_unifirst === true || (product.attributes as any)?.made_by_unifirst === 'Yes';
  const canadaOnly = (product.attributes as any)?.canada_only === true || (product.attributes as any)?.canada_only === 'Yes';
  const dsValue = (product.attributes as any)?.Direct_Sale_Only_or_Both;
  const directSaleOnly = dsValue === 'Direct Sale' || dsValue === 'Direct Sale Only';
  const salesChannelUnknown = !dsValue;

  // Get description - prioritize long_description
  const description = (product.attributes as any)?.long_description || 
                     (product.attributes as any)?.mozart_description || 
                     product.description;
                     
  // Semantic alternative indicator - products from pure semantic search (no filter matches)
  const isSemanticAlternative = product.is_semantic_alternative || (product as any).is_semantic_alternative;
  
  // Curated related product indicator - teal badge for catalog-related products
  const isCuratedRelated = product.is_curated_related || (product as any).is_curated_related;

  return (
    <div
      ref={cardRef}
      className={`w-full border rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200 relative ${isSemanticAlternative ? 'bg-gray-50 border-gray-300' : 'bg-white'}`}
      style={{ minHeight: '640px' }}
      title={isSemanticAlternative ? 'Pure Semantic Search result - found via AI-powered meaning search, not exact filter match' : undefined}
    >
      {/* Curated Related Badge - purple badge in bottom-left corner */}
      {isCuratedRelated && (
        <div 
          className="absolute bottom-3 left-3"
          style={{ zIndex: 10 }}
          title="Curated related product from catalog"
        >
          <div 
            className="flex items-center gap-1.5 bg-purple-100 text-purple-700 rounded-full shadow-sm px-2 py-1 border border-purple-200"
            style={{ fontSize: '11px' }}
          >
            <svg 
              className="w-3 h-3"
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-medium">Curated</span>
          </div>
        </div>
      )}
      
      {/* Semantic Search Indicator - bottom-right corner */}
      {isSemanticAlternative && (
        <div 
          className="absolute bottom-3 right-3"
          style={{ zIndex: 10 }}
          title="Pure Semantic Search result"
        >
          <div 
            className="flex items-center gap-1.5 bg-gray-200 text-gray-600 rounded-full shadow-sm px-2 py-1"
            style={{ fontSize: '11px' }}
          >
            <svg 
              className="w-3 h-3"
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span className="font-medium">AI</span>
          </div>
        </div>
      )}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left side - Image and Color Variants */}
        <div className="flex-shrink-0 w-full lg:w-[340px]">
          {/* Header */}
          <div className="mb-3">
            <div className="flex justify-between items-start mb-2 gap-3">
              <h3 className="text-lg font-semibold text-gray-900 flex-1 min-w-0">{product.name}</h3>
              {product.price && <div className="text-base font-bold text-teal-600 whitespace-nowrap flex-shrink-0">SC: ${product.price}</div>}
            </div>
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-500">Style: {product.attributes?.style_code || sku?.substring(0, 4) || sku}</div>
              {(selected?.vendor_sku || (product.attributes as any)?.vendor_sku) && (
                <div className="text-sm text-gray-400">Vendor SKU: {selected?.vendor_sku || (product.attributes as any).vendor_sku}</div>
              )}
            </div>
          </div>

          {/* Badges */}
          {(badges.length > 0 || stockItems > 0 || canadaOnly || directSaleOnly || salesChannelUnknown) && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {badges.map((badge: string, idx: number) => {
                const isMadeByUniFirst = badge === 'Made by UniFirst';
                const isBestseller = badge.includes('Bestseller') || badge.includes('⭐');
                return (
                  <span 
                    key={idx}
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      isBestseller
                        ? 'bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-900 border border-yellow-300 shadow-sm'
                        : isMadeByUniFirst 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-teal-100 text-teal-800'
                    }`}
                  >
                    {badge}
                  </span>
                );
              })}
              {stockItems > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  {stockItems} in stock
                </span>
              )}
              {canadaOnly && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  CAN Only
                </span>
              )}
              {directSaleOnly && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  Direct Sale Only
                </span>
              )}
              {salesChannelUnknown && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                  Sales Channel Unknown
                </span>
              )}
            </div>
          )}

          {/* Image */}
          <div className="w-full flex justify-center mb-3 min-h-[280px] max-h-[320px] relative overflow-hidden rounded-lg bg-gray-50">
            {/* Show fallback only while loading or on error */}
            {(!imageLoaded || imageError) && (
              <div className="w-full h-full flex items-center justify-center">
                <img 
                  src={fallbackImageUrl} 
                  alt={`${product.name} (fallback)`} 
                  className="max-h-56 max-w-56 opacity-40 object-contain"
                />
              </div>
            )}
            
            {/* Show loading overlay */}
            {imageIsLoading && !imageLoaded && !imageError && (
              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-md">
                <div className="animate-pulse text-gray-500 bg-white px-3 py-1 rounded-full shadow-sm">
                  Loading image...
                </div>
              </div>
            )}
            
            {/* Show the real image on top when loaded */}
            {imageUrl && !imageError && imageLoaded && (
              <img 
                src={imageUrl} 
                alt={product.name} 
                className="absolute inset-0 w-full h-full object-contain transition-opacity duration-300 opacity-100 p-2"
                loading="lazy"
                style={{ 
                  zIndex: 10,
                  maxWidth: '100%',
                  maxHeight: '100%'
                }}
              />
            )}
          </div>

          {/* Color Variants */}
          {product.color_variants && product.color_variants.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {product.color_variants.map((v: ColorVariant) => {
                const sanitizedSwatchUrl = sanitizeUrl(v.swatch_url || undefined);
                const swatchUrl = getProxiedImageUrl(sanitizedSwatchUrl);
                // IMPORTANT: Check the proxied URL (what's actually rendered and preloaded)
                const swatchLoaded = swatchUrl ? isImageLoaded(swatchUrl) : false;
                const swatchError = swatchUrl ? getImageError(swatchUrl) : false;
                
                return (
                  <button
                    key={v.sku}
                    title={v.color_name && v.color_code ? `${v.color_name} (${v.color_code})` : v.color_name || v.color_code || v.sku}
                    className={`p-0.5 rounded-full border transition-all duration-150 ${
                      selected?.sku === v.sku 
                        ? 'border-teal-500 shadow-md' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleVariantSelect(v)}
                  >
                    <div 
                      className="w-7 h-7 rounded-full overflow-hidden" 
                      style={{ backgroundColor: v.color_code || '#cccccc' }}
                    >
                      {swatchUrl && !swatchError && swatchLoaded ? (
                        <img 
                          src={swatchUrl} 
                          alt={v.color_name || v.color_code || ''} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div 
                          className="w-full h-full" 
                          style={{ backgroundColor: v.color_code || '#cccccc' }} 
                          title={`${v.color_name || v.color_code || 'Color'} swatch`}
                        />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Selected color label */}
          {selected && (selected.color_name || selected.color_code) && (
            <div className="text-xs text-gray-500 mt-1">
              {selected.color_name && selected.color_code 
                ? `${selected.color_name} (${selected.color_code})`
                : selected.color_name || selected.color_code}
            </div>
          )}

          {/* Size Options */}
          {product.attributes?.sizes && product.attributes.sizes !== 'Not specified' && (
            <div className="mt-2">
              <div className="text-gray-500 text-xs font-medium mb-1">Sizes</div>
              <div className="flex flex-wrap gap-1">
                {(() => {
                  const sizesStr = product.attributes.sizes;
                  // Parse size ranges like "XS–4XL" or "S–3XL" into individual sizes
                  const allSizes = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL'];
                  const rangeMatch = sizesStr.match(/^([A-Z0-9]+)[–-]([A-Z0-9]+)$/i);
                  
                  let displaySizes: string[] = [];
                  if (rangeMatch) {
                    const startSize = rangeMatch[1].toUpperCase();
                    const endSize = rangeMatch[2].toUpperCase();
                    const startIdx = allSizes.findIndex(s => s === startSize);
                    const endIdx = allSizes.findIndex(s => s === endSize);
                    if (startIdx !== -1 && endIdx !== -1 && startIdx <= endIdx) {
                      displaySizes = allSizes.slice(startIdx, endIdx + 1);
                    } else {
                      // Fallback: just show the original string
                      displaySizes = [sizesStr];
                    }
                  } else if (sizesStr.includes(',')) {
                    // Comma-separated sizes
                    displaySizes = sizesStr.split(',').map((s: string) => s.trim()).filter(Boolean);
                  } else {
                    // Single size or unrecognized format
                    displaySizes = [sizesStr];
                  }
                  
                  return displaySizes.map((size: string, idx: number) => (
                    <span
                      key={idx}
                      className="inline-flex items-center justify-center px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-700 text-xs font-medium min-w-[24px]"
                    >
                      {size}
                    </span>
                  ));
                })()}
              </div>
            </div>
          )}
        </div>

        {/* Right side - ALL Product Details */}
        <div className="flex-1 flex flex-col min-w-0 overflow-y-auto" style={{ maxHeight: '600px' }}>
          {/* === DESCRIPTION === */}
          <div className="mb-3 pb-2 border-b border-gray-200">
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Description</h4>
            <p className="text-sm text-gray-600 leading-relaxed">
              {description || <span className="text-gray-400 italic">Not specified</span>}
            </p>
          </div>

          {/* === PRODUCT IDENTITY / SKUs === */}
          <div className="mb-3 pb-2 border-b border-gray-200">
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Product Identity</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
              <div>
                <span className="font-medium text-gray-600">Style Code:</span>{' '}
                <span className={product.attributes?.style_code ? "text-gray-800" : "text-gray-400 italic"}>
                  {product.attributes?.style_code || 'N/A'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Partial SKU:</span>{' '}
                <span className={selected?.sku || (product as any).partialSku_OPIM || product.base_sku ? "text-gray-800" : "text-gray-400 italic"}>
                  {selected?.sku || (product as any).partialSku_OPIM || product.base_sku || 'N/A'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Catalog SKU:</span>{' '}
                <span className={(product.attributes as any)?.catalog_sku ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.catalog_sku || 'N/A'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Original SKU:</span>{' '}
                <span className={(product.attributes as any)?.original_sku ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.original_sku || 'N/A'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Matched SKU:</span>{' '}
                <span className={(product.attributes as any)?.matched_sku ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.matched_sku || 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* === CATEGORIES === */}
          <div className="mb-3 pb-2 border-b border-gray-200">
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Categories</h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="font-medium text-gray-600">Class:</span>{' '}
                <span className={(product.attributes as any)?.class_description ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.class_description || 'Not specified'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Sub-Class:</span>{' '}
                <span className={(product.attributes as any)?.sub_class_description ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.sub_class_description || 'Not specified'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Product Type:</span>{' '}
                <span className={(product.attributes as any)?.product_sub_type_category || product.attributes?.category ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.product_sub_type_category || product.attributes?.category || 'Not specified'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Industry:</span>{' '}
                <span className={(product.attributes as any)?.industry ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.industry || 'Not specified'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Use Case:</span>{' '}
                <span className={(product.attributes as any)?.spec_app_description ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.spec_app_description || 'Not specified'}
                </span>
              </div>
            </div>
          </div>

          {/* === ATTRIBUTES === */}
          <div className="mb-3 pb-2 border-b border-gray-200">
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Attributes</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
              <div>
                <span className="font-medium text-gray-600">Gender:</span>{' '}
                <span className={(product.attributes as any)?.gender ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.gender || 'Not specified'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Sizes:</span>{' '}
                <span className={(product.attributes as any)?.sizes ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.sizes || 'Not specified'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Color Keywords:</span>{' '}
                <span className={(product.attributes as any)?.color_keywords ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.color_keywords || 'Not specified'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Variety:</span>{' '}
                <span className={(product.attributes as any)?.variety ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.variety || 'Not specified'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Line:</span>{' '}
                <span className={(product.attributes as any)?.line ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.line || 'Not specified'}
                </span>
              </div>
            </div>
          </div>

          {/* === FABRIC & MATERIALS === */}
          <div className="mb-3 pb-2 border-b border-gray-200">
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Fabric & Materials</h4>
            <div className="space-y-1 text-xs">
              <div>
                <span className="font-medium text-gray-600">Fabric Description:</span>{' '}
                <span className={(product.attributes as any)?.fabric_description ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.fabric_description || 'Not specified'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Fabrics:</span>{' '}
                <span className={(product.attributes as any)?.fabrics ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.fabrics || 'Not specified'}
                </span>
              </div>
            </div>
          </div>

          {/* === SAFETY & COMPLIANCE === */}
          <div className="mb-3 pb-2 border-b border-gray-200">
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Safety & Compliance</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
              <div>
                <span className="font-medium text-gray-600">Fire Resistant:</span>{' '}
                <span className={(product.attributes as any)?.fire_resistant !== undefined ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.fire_resistant === true ? 'Yes' : 
                   (product.attributes as any)?.fire_resistant === false ? 'No' : 'Not specified'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Hi-Visibility:</span>{' '}
                <span className={(product.attributes as any)?.hi_visibility ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.hi_visibility || 'Not specified'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Enhanced Visibility:</span>{' '}
                <span className={(product.attributes as any)?.enhanced_vis !== undefined || (product.attributes as any)?.enhanced_visibility !== undefined ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.enhanced_vis === true || (product.attributes as any)?.enhanced_visibility === true ? 'Yes' : 
                   (product.attributes as any)?.enhanced_vis === false || (product.attributes as any)?.enhanced_visibility === false ? 'No' : 'Not specified'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">ANSI Class:</span>{' '}
                <span className={(product.attributes as any)?.ansi_class ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.ansi_class || 'Not specified'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">CAT Rating:</span>{' '}
                <span className={(product.attributes as any)?.cat_rating ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.cat_rating || 'Not specified'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">ATPV:</span>{' '}
                <span className={(product.attributes as any)?.atpv ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.atpv ? `${(product.attributes as any).atpv} cal/cm²` : 'Not specified'}
                </span>
              </div>
              <div className="col-span-2 md:col-span-3">
                <span className="font-medium text-gray-600">Certifications:</span>{' '}
                <span className={(product.attributes as any)?.certifications ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.certifications || 'Not specified'}
                </span>
              </div>
            </div>
          </div>

          {/* === BRAND & VENDOR === */}
          <div className="mb-3 pb-2 border-b border-gray-200">
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Brand & Vendor</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
              <div>
                <span className="font-medium text-gray-600">Brand:</span>{' '}
                <span className={(product.attributes as any)?.parent_brand ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.parent_brand || 'Not specified'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Vendor:</span>{' '}
                <span className="text-gray-800">
                  {madeByUniFirst ? 'UniFirst' : ((product.attributes as any)?.vendorname || (product.attributes as any)?.vendor_name || 'Not specified')}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Vendor SKU:</span>{' '}
                <span className={selected?.vendor_sku || (product.attributes as any)?.vendor_sku ? "text-gray-800" : "text-gray-400 italic"}>
                  {selected?.vendor_sku || (product.attributes as any)?.vendor_sku || 'Not specified'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Made by UniFirst:</span>{' '}
                <span className={madeByUniFirst ? "text-blue-600 font-medium" : "text-gray-400 italic"}>
                  {madeByUniFirst ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          </div>

          {/* === SALES & AVAILABILITY === */}
          <div className="mb-3 pb-2 border-b border-gray-200">
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Sales & Availability</h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="font-medium text-gray-600">Standard Cost:</span>{' '}
                <span className={product.price ? "text-teal-600 font-bold" : "text-gray-400 italic"}>
                  {product.price ? `$${product.price}` : 'Not specified'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Stock Items:</span>{' '}
                <span className={stockItems > 0 ? "text-green-600 font-medium" : "text-gray-400 italic"}>
                  {stockItems > 0 ? `${stockItems} units` : 'Not specified'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Sales Channel:</span>{' '}
                <span className={(product.attributes as any)?.Direct_Sale_Only_or_Both ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.Direct_Sale_Only_or_Both || 'Not specified'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Canada Only:</span>{' '}
                <span className={canadaOnly ? "text-red-600 font-medium" : "text-gray-400 italic"}>
                  {canadaOnly ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          </div>

          {/* === TAGS & RELATED === */}
          <div className="mb-3 pb-2 border-b border-gray-200">
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Tags & Related</h4>
            <div className="space-y-1 text-xs">
              <div>
                <span className="font-medium text-gray-600">Tags:</span>{' '}
                <span className={(product.attributes as any)?.tags_attributes ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.tags_attributes || 'Not specified'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Related Products:</span>{' '}
                <span className={(product.attributes as any)?.related_products ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.related_products || 'Not specified'}
                </span>
              </div>
            </div>
          </div>

          {/* === AVAILABLE COLORS === */}
          <div className="mb-3 pb-2 border-b border-gray-200">
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Available Colors</h4>
            <div className="text-xs">
              <span className={product.color_variants && product.color_variants.length > 0 ? "text-gray-800" : "text-gray-400 italic"}>
                {product.color_variants && product.color_variants.length > 0 
                  ? product.color_variants.map((v: ColorVariant) => v.color_name || v.color_code).filter(Boolean).join(', ') || `${product.color_variants.length} variant(s)`
                  : 'Single color only'}
              </span>
            </div>
          </div>

          {/* === STYLE DESCRIPTION === */}
          <div className="mb-3 pb-2 border-b border-gray-200">
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Style Description</h4>
            <div className="text-xs">
              <span className={(product.attributes as any)?.style_desc || (product.attributes as any)?.style_description ? "text-gray-800" : "text-gray-400 italic"}>
                {(product.attributes as any)?.style_desc || (product.attributes as any)?.style_description || 'Not specified'}
              </span>
            </div>
          </div>

          {/* Why This Product (capped at 4) */}
          {reasons.length > 0 && (
            <div className="mt-auto pt-3 border-t border-gray-100">
              {/* Mobile/Tablet: Collapsible */}
              <div className="lg:hidden">
                <button
                  onClick={() => setIsReasonsExpanded(!isReasonsExpanded)}
                  className="w-full flex items-center justify-between text-sm font-semibold text-gray-700 mb-1 hover:text-teal-600 transition-colors"
                >
                  <span>Differentiating Features:</span>
                  <svg
                    className={`w-4 h-4 transition-transform ${isReasonsExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isReasonsExpanded && (
                  <ul className="space-y-1 mt-1">
                    {reasons.slice(0, 4).map((reason: string, idx: number) => (
                      <li key={idx} className="text-sm leading-5 text-gray-700 flex items-start">
                        <span className="text-teal-600 mr-1.5 mt-0.5">•</span>
                        <span>{reason}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              
              {/* Desktop: Always Expanded */}
              <div className="hidden lg:block">
                <div className="text-sm font-semibold text-gray-700 mb-1">Differentiating Features:</div>
                <ul className="space-y-1">
                  {reasons.slice(0, 4).map((reason: string, idx: number) => (
                    <li key={idx} className="text-sm leading-5 text-gray-700 flex items-start">
                      <span className="text-teal-600 mr-1.5 mt-0.5">•</span>
                      <span>{reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Memoize to avoid re-renders while streaming text updates
const ProductCardDetailed = React.memo(ProductCardDetailedComponent);

export default ProductCardDetailed;

